import { useQuery } from "@tanstack/react-query";
import { api } from "../api.js";

/**
 * What's new (ADR 0024): each release as a customer reads it — what it added and what it fixed, from the same commit
 * subjects the release notes are made of. The chores and the docs are left out, and so is a release with nothing else.
 * The cheapest way to show someone the product moving without emailing them.
 */
export function WhatsNew() {
  const q = useQuery({ queryKey: ["whats-new"], queryFn: api.whatsNew, staleTime: 5 * 60_000 });
  if (q.error) return <p role="alert" className="text-[13px] text-bad">{String(q.error)}</p>;
  if (!q.data) return <p className="text-[13px] text-muted">…</p>;
  const { versions } = q.data;
  if (!versions.length) {
    return <p className="text-[13px] text-muted py-6">Nothing to show yet. The first release with something new in it will be listed here.</p>;
  }
  return (
    <div className="flex flex-col gap-5 max-w-[620px]">
      {versions.map((v) => (
        <section key={v.version}>
          <div className="flex items-baseline gap-2">
            <h3 className="text-[14px] font-semibold">Version {v.version}</h3>
            <span className="text-[11.5px] text-muted">{new Date(v.at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}</span>
          </div>
          {v.added.length ? <Changes title="New" items={v.added} /> : null}
          {v.fixed.length ? <Changes title="Fixed" items={v.fixed} /> : null}
        </section>
      ))}
    </div>
  );
}

function Changes({ title, items }: { title: string; items: string[] }) {
  return (
    <div className="mt-2">
      <h4 className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted mb-1">{title}</h4>
      <ul className="flex flex-col gap-1">
        {items.map((item, i) => (
          <li key={`${i}-${item}`} className="flex gap-2 text-[13px] leading-relaxed">
            <span className="text-coral" aria-hidden>•</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
